import { AxiosInstance } from 'axios';
import { Pageable, PagedResult } from './pageable';
import { handleErrorResponse, handleSuccessResponse } from "./errorHandling";
import { EntityRef } from "./entities";

export const apiHost = '/api';

export type CrudApi<T, Q> = {
  queryList?: (axios: AxiosInstance, params?: string) => Promise<T[]>,
  queryPage?: (axios: AxiosInstance, pageable: Pageable, params?: string) => Promise<PagedResult<T>>,
  queryOne: (axios: AxiosInstance, id: string | number) => Promise<Q>,
  createOne: (axios: AxiosInstance, entity: Q) => Promise<Q>,
  updateOne: (axios: AxiosInstance, entity: Q) => Promise<Q>,
  deleteOne: (axios: AxiosInstance, id: string | number) => Promise<void>,
}

export function queryPage<DTO>(customAxios: AxiosInstance, relativeUri: string, pageable: Pageable, params?: string): Promise<PagedResult<DTO>> {
  const baseUri = apiHost + '/' + relativeUri;
  return customAxios.get(`${baseUri}?${toUrlParams(pageable)}` + (params ? '&' + params : ''))
    .then(handleSuccessResponse, handleErrorResponse);
}

export function queryList<DTO>(customAxios: AxiosInstance, relativeUri: string, params?: string): Promise<DTO[]> {
  const baseUri = apiHost + '/' + relativeUri;
  return customAxios.get(`${baseUri}?` + (params ? params : ''))
    .then(handleSuccessResponse, handleErrorResponse);
}

export function queryOne<DTO>(customAxios: AxiosInstance, relativeUri: string, id?: string | number, params?: string): Promise<DTO> {
  const baseUri = apiHost + '/' + relativeUri;
  const url = id ? `${baseUri}/${id}` : baseUri;
  return customAxios.get(url + (params ? '?' + params : ''))
    .then(handleSuccessResponse, handleErrorResponse);
}

export function queryFile(customAxios: AxiosInstance, relativeUri: string, id?: string | number, params?: string, mediaType?: string): Promise<File> {
  const baseUri = relativeUri.startsWith('http') ? relativeUri : apiHost + '/' + relativeUri;
  let headers: any = {};
  if (mediaType) {
    headers.accept = mediaType;
  }
  const url = id ? `${baseUri}/${id}` : baseUri;
  return customAxios.request({ url: url + (params ? '?' + params : ''), method: 'GET', responseType: 'blob', headers: headers })
    .then((response) => {
      let fileName = "file";
      if (response.headers["content-disposition"] && response.headers["content-disposition"].includes("filename=")) {
        fileName = response.headers["content-disposition"].split("filename=")[1].replaceAll("\"", "");
      }
      return Promise.resolve(new File([ response.data as Blob ], fileName))
    }, (error) => {
      throw new Error(error.message)
    });
}

export function createOne<RequestDTO, ResponseDTO = RequestDTO>(customAxios: AxiosInstance, relativeUri: string, entity: RequestDTO): Promise<ResponseDTO> {
  const baseUri = apiHost + '/' + relativeUri;
  return customAxios.post(baseUri, entity)
    .then(handleSuccessResponse, handleErrorResponse);
}

export function updateOne<DTO extends EntityRef>(customAxios: AxiosInstance, relativeUri: string, entity: DTO): Promise<DTO> {
  const baseUri = apiHost + '/' + relativeUri;
  return customAxios.put(`${baseUri}/${entity.id}`, entity)
    .then(handleSuccessResponse, handleErrorResponse);
}

export function deleteOne(customAxios: AxiosInstance, relativeUri: string, id: string | number): Promise<void> {
  const baseUri = apiHost + '/' + relativeUri;
  return customAxios.delete(`${baseUri}/${id}`)
    .then(handleSuccessResponse, handleErrorResponse);
}

export function toUrlParams(pageable: Pageable) {
  let params = `page=${pageable.page}&size=${pageable.size}`;
  if (pageable.search) {
    params += `&search=${pageable.search}`;
  }
  if (pageable.sort) {
    params += `&sort=${pageable.sort.field},${pageable.sort.order}`;
  }
  return params;
}

export function toUrlParamsForQuery(pageable?: Pageable, search?: string) {
  const searchParams = search ? 'search=' + encodeURIComponent(search) : undefined;
  const pageParams = pageable ? toUrlParams(pageable) : undefined;
  return [ searchParams, pageParams ].filter(p => p).join('&');
}
